import { OrganizationRef, UserRef } from './base';

/**
 * Post Type models and DTOs
 * 
 * These models correspond to the Post Type API endpoints defined in POSTTYPE_API_REFERENCE.md
 */

/**
 * Base Post Type interface matching API data model
 * 
 * @remarks
 * This represents the core post type structure as returned by the API.
 * Post types are used to categorize blog posts within an organization.
 * 
 * @example
 * ```typescript
 * const postType: PostType = {
 *   _id: '507f1f77bcf86cd799439011',
 *   typeName: 'Tutorial',
 *   organizationId: '507f1f77bcf86cd799439012',
 *   createdBy: '507f1f77bcf86cd799439013',
 *   isActive: true,
 *   createdAt: new Date('2024-01-15T10:00:00Z'),
 *   updatedAt: new Date('2024-01-15T10:00:00Z')
 * };
 * ```
 */
export interface PostType {
  /**
   * Unique identifier (MongoDB ObjectId)
   */
  _id: string;

  /**
   * Name of the post type (2-50 characters)
   * Must be unique within the organization
   */
  typeName: string;

  /**
   * ID of the organization this post type belongs to
   */
  organizationId: string;

  /**
   * ID of the user who created this post type
   */
  createdBy: string;

  /**
   * Whether the post type is active (soft delete flag)
   * @default true
   */
  isActive: boolean;

  /**
   * Whether the post type is visible in public-facing endpoints
   * @default false
   */
  isPublic?: boolean;

  /**
   * Timestamp when the post type was created
   */
  createdAt: Date;

  /**
   * Timestamp when the post type was last updated
   */
  updatedAt: Date;
}

/**
 * Post Type with populated references
 * 
 * @remarks
 * This interface represents a post type with its related entities fully populated
 * as returned by GET endpoints. The organizationId and createdBy fields are
 * replaced with their full object representations.
 * 
 * @example
 * ```typescript
 * const postType: PostTypePopulated = {
 *   _id: '507f1f77bcf86cd799439011',
 *   typeName: 'Tutorial',
 *   organizationId: {
 *     _id: '507f1f77bcf86cd799439012',
 *     name: 'Tech Blog Organization'
 *   },
 *   createdBy: {
 *     _id: '507f1f77bcf86cd799439013',
 *     name: 'John Doe',
 *     email: 'john@example.com'
 *   },
 *   isActive: true,
 *   createdAt: new Date('2024-01-15T10:00:00Z'),
 *   updatedAt: new Date('2024-01-15T10:00:00Z')
 * };
 * ```
 */
export interface PostTypePopulated extends Omit<PostType, 'organizationId' | 'createdBy'> {
  /**
   * Populated organization reference
   */
  organizationId: OrganizationRef;

  /**
   * Populated user reference
   */
  createdBy: UserRef;
}

/**
 * DTO for creating a new post type
 * 
 * @remarks
 * Used with POST /api/post-types endpoint.
 * Type name must be unique within the organization.
 * User must be a member of the organization.
 * 
 * @example
 * ```typescript
 * const createDto: CreatePostTypeDto = {
 *   typeName: 'Tutorial',
 *   organizationId: '507f1f77bcf86cd799439012'
 * };
 * 
 * postTypeService.createPostType(createDto).subscribe(
 *   response => console.log('Created:', response.data)
 * );
 * ```
 */
export interface CreatePostTypeDto {
  /**
   * Name of the post type (2-50 characters)
   * Must be unique within the organization
   */
  typeName: string;

  /**
   * ID of the organization this post type belongs to
   */
  organizationId: string;

  /**
   * Whether the post type is visible in public-facing endpoints
   * Omit to default to false on the server
   */
  isPublic?: boolean;
}

/**
 * DTO for updating an existing post type
 * 
 * @remarks
 * Used with PUT /api/post-types/:id endpoint.
 * Only the type name can be updated.
 * Type name must remain unique within the organization.
 * 
 * @example
 * ```typescript
 * const updateDto: UpdatePostTypeDto = {
 *   typeName: 'Advanced Tutorial'
 * };
 * 
 * postTypeService.updatePostType('507f1f77bcf86cd799439011', updateDto)
 *   .subscribe(response => console.log('Updated:', response.data));
 * ```
 */
export interface UpdatePostTypeDto {
  /**
   * Updated name for the post type (2-50 characters)
   * Must be unique within the organization
   */
  typeName?: string; // optional to allow isPublic-only updates

  /**
   * Update public visibility of the post type
   * Omit to leave the existing value unchanged
   */
  isPublic?: boolean;
}

/**
 * Query parameters for fetching post types
 * 
 * @remarks
 * Used with GET /api/post-types endpoint.
 * Without organizationId: Returns all post types from user's created organizations
 * With organizationId: Returns post types for specified organization (if user has access)
 * 
 * @example
 * ```typescript
 * // Get all post types for an organization
 * const params: PostTypeQueryParams = {
 *   organizationId: '507f1f77bcf86cd799439012'
 * };
 * 
 * postTypeService.getAllPostTypes(params).subscribe(
 *   response => console.log('Post types:', response.data)
 * );
 * ```
 */
export interface PostTypeQueryParams {
  /**
   * Optional: Filter by organization ID
   * If not provided, returns all post types from user's created organizations
   */
  organizationId?: string;
}

/**
 * Standard API response for single post type operations
 * 
 * @remarks
 * Returned by POST, GET (single), and PUT endpoints
 */
export interface PostTypeResponse {
  success: boolean;
  data: PostTypePopulated;
  message?: string;
}

/**
 * API response for listing post types
 * 
 * @remarks
 * Returned by GET /api/post-types endpoint
 * Includes count of returned items
 */
export interface PostTypeListResponse {
  success: boolean;
  count: number;
  data: PostTypePopulated[];
}

/**
 * Lightweight post type item returned by the public list endpoint
 *
 * @remarks
 * Returned by GET /api/post-types/public?orgId={orgId}
 */
export interface PublicPostTypeItem {
  _id: string;
  typeName: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * API response for the public lightweight post type list
 *
 * @remarks
 * Returned by GET /api/post-types/public?orgId={orgId}.
 * Contains only isActive && isPublic post types.
 */
export interface PublicPostTypeListResponse {
  success: boolean;
  count: number;
  organizationId: string;
  data: PublicPostTypeItem[];
}

/**
 * Lightweight active post type item returned by the authenticated active list endpoint
 *
 * @remarks
 * Returned by GET /api/post-types/active.
 * Contains only _id and typeName for each active post type.
 */
export interface ActivePostTypeItem {
  _id: string;
  typeName: string;
}

/**
 * API response for the authenticated active post type list
 *
 * @remarks
 * Returned by GET /api/post-types/active.
 * Organization is derived from the JWT orgId claim — no query params needed.
 * Results are sorted alphabetically by typeName.
 */
export interface ActivePostTypeListResponse {
  success: boolean;
  data: ActivePostTypeItem[];
}

/**
 * API response for delete operations
 *
 * @remarks
 * Returned by DELETE /api/post-types/:id endpoint
 * Note: This is a soft delete (sets isActive to false)
 */
export interface PostTypeDeleteResponse {
  success: boolean;
  message: string;
}

/**
 * Post Type API error response
 * 
 * @remarks
 * Returned when an error occurs during API operations
 * Includes specific error messages for common scenarios
 */
export interface PostTypeError {
  success: false;
  message: string;
  statusCode?: number;
  errors?: Record<string, string[]>;
}

/**
 * Type guard to check if a response is a PostTypeError
 * 
 * @param response - The API response to check
 * @returns True if the response is an error response
 * 
 * @example
 * ```typescript
 * postTypeService.createPostType(dto).subscribe({
 *   next: (response) => {
 *     if (isPostTypeError(response)) {
 *       console.error('Error:', response.message);
 *     } else {
 *       console.log('Success:', response.data);
 *     }
 *   }
 * });
 * ```
 */
export function isPostTypeError(response: any): response is PostTypeError {
  return response && response.success === false && typeof response.message === 'string';
}
